// shared/products/performance/roles.ts
import type { SystemRoleDefinition } from "../../roles";
import { PERFORMANCE_ROLE_NAMES } from "./roleNames";

/**
 * The skyPerformance role catalog (spec §4.3 as amended 2026-10-03; D2'). Three roles, immutable,
 * instance-scoped; LEVEL is data about the person (perf_people), not a role. Labels here are the
 * neutral defaults; the vertical pack's `roles.*` labels are what screens show.
 *
 * First-cut permissions against the wireframe's entities (analysis §D item 1). The per-role
 * matrix is refined when the persona designs arrive; Role Management shows these read-only.
 * Level-gated behaviour (running a site visit) is a permission AND a config threshold on level:
 * the threshold is the pack's, the permission is the role's.
 */
export const PERFORMANCE_PERMISSIONS = {
  // The landing, config and scope reads: every performance role holds it.
  "performance.home.view": true,
  "performance.coaching.view": true,
  "performance.coaching.create": true,
  "performance.coaching.acknowledge": true,
  "performance.todos.view": true,
  "performance.todos.manage": true,
  "performance.plans.view": true,
  "performance.plans.acknowledge": true,
  "performance.plans.create": true,
  "performance.plans.approve": true,
  "performance.plans.activate": true,
  "performance.plans.close": true,
  "performance.evaluations.view": true,
  "performance.evaluations.create": true,
  "performance.evaluations.score": true,
  "performance.evaluations.acknowledge": true,
  "performance.visits.view": true,
  "performance.visits.run": true,
  "performance.org.view": true,
  "performance.records.view": true,
  "performance.records.delete": true,
  "performance.records.export": true,
  "performance.documents.view": true,
  "performance.documents.export": true,
  "performance.settings.manage": true,
  "performance.users.manage": true,
} as const;

export const PERFORMANCE_PERMISSION_KEYS: readonly string[] = Object.keys(PERFORMANCE_PERMISSIONS);

export const PERFORMANCE_NAVIGATION = {
  showPerfHome: true,
  showPerfCoaching: true,
  showPerfTodos: true,
  showPerfPlans: true,
  showPerfEvaluations: true,
  showPerfVisits: true,
  showPerfOrg: true,
  showPerfRecords: true,
  showPerfDocuments: true,
  showPerfSettings: true,
  // E2 D7: "My file", on for every role (a manager and HR have a file too, A-13).
  showPerfFile: true,
} as const;

export const PERFORMANCE_NAVIGATION_KEYS: readonly string[] = Object.keys(PERFORMANCE_NAVIGATION);

type PermissionMap = Record<keyof typeof PERFORMANCE_PERMISSIONS, boolean>;
type NavigationMap = Record<keyof typeof PERFORMANCE_NAVIGATION, boolean>;

const allOff = <T extends Record<string, boolean>>(map: T): Record<keyof T, boolean> =>
  Object.fromEntries(Object.keys(map).map((k) => [k, false])) as Record<keyof T, boolean>;

/**
 * Self only: reads their own file, acknowledges, works their to-dos (wireframe `core.js:158-160`).
 * No org chart (E7 O-4, operator-approved 2026-10-06, superseding D1): the employee's sidebar is
 * exactly the wireframe's (core.js:231-237), and the chart route refuses them.
 */
const EMPLOYEE_PERMISSIONS: PermissionMap = {
  ...allOff(PERFORMANCE_PERMISSIONS),
  "performance.home.view": true,
  "performance.coaching.view": true,
  "performance.coaching.acknowledge": true,
  "performance.todos.view": true,
  "performance.todos.manage": true,
  "performance.plans.view": true,
  "performance.plans.acknowledge": true,
  "performance.evaluations.view": true,
  "performance.evaluations.acknowledge": true,
  "performance.records.view": true,
  "performance.documents.view": true,
};

const EMPLOYEE_NAVIGATION: NavigationMap = {
  ...allOff(PERFORMANCE_NAVIGATION),
  showPerfHome: true,
  showPerfCoaching: true,
  showPerfTodos: true,
  showPerfRecords: true,
  showPerfDocuments: true,
  showPerfFile: true,
};

/**
 * Self + everyone below in the reporting line. Deletion, the records and documents exports (E7 D5,
 * E8) and settings stay with HR; the coaching CSV is gated on performance.coaching.view, so a
 * manager exports their own reach (E1 A-38, A-39). E3 W-10
 * (D1, O-2): a manager approves the plan step named for them (the chain runs up the starter's line),
 * so they hold performance.plans.approve; the plan routes keep each decision to its named approver.
 */
const MANAGER_PERMISSIONS: PermissionMap = {
  ...PERFORMANCE_PERMISSIONS,
  "performance.records.delete": false,
  "performance.records.export": false,
  "performance.documents.export": false,
  "performance.settings.manage": false,
  "performance.users.manage": false,
};

const MANAGER_NAVIGATION: NavigationMap = {
  ...PERFORMANCE_NAVIGATION,
  showPerfSettings: false,
  // E2 O-1 (operator-approved 2026-10-06): My file is the employee's item, as the wireframe's navs
  // (core.js:231-262). A manager opens every file, their own included, from the Org chart.
  showPerfFile: false,
};

/** HR: every item but My file (E2 O-1), which it reaches from the Org chart like a manager. */
const HR_NAVIGATION: NavigationMap = {
  ...PERFORMANCE_NAVIGATION,
  showPerfFile: false,
};

export const PERFORMANCE_ROLES: SystemRoleDefinition[] = [
  {
    name: PERFORMANCE_ROLE_NAMES.employee,
    label: "Employee",
    description:
      "Sees only their own file, acknowledges what was documented, works their to-do list. The default for anyone with nobody reporting to them.",
    category: "Staff",
    scope: "instance",
    product: "skyperformance",
    dataScope: "none",
    isSystem: true,
    isHidden: false,
    isImmutable: true,
    permissions: EMPLOYEE_PERMISSIONS,
    navigation: EMPLOYEE_NAVIGATION,
  },
  {
    name: PERFORMANCE_ROLE_NAMES.manager,
    label: "Manager",
    description:
      "Documents coaching, runs evaluations and starts improvement plans for everyone below them in the reporting line. Their level decides what they can reach beyond that.",
    category: "Manager",
    scope: "instance",
    product: "skyperformance",
    dataScope: "none",
    isSystem: true,
    isHidden: false,
    isImmutable: true,
    permissions: MANAGER_PERMISSIONS,
    navigation: MANAGER_NAVIGATION,
  },
  {
    name: PERFORMANCE_ROLE_NAMES.hr,
    label: "HR",
    description:
      "The performance administrator: approves plans, sees every file, runs exports, configures the product and manages performance access.",
    category: "Admin",
    scope: "instance",
    product: "skyperformance",
    dataScope: "none",
    isSystem: true,
    isHidden: false,
    isImmutable: true,
    permissions: { ...PERFORMANCE_PERMISSIONS },
    navigation: { ...HR_NAVIGATION },
  },
];
